//
// MetadataPanel
//
// A panel for the metadata of schema items and assets

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useMousePopover } from '../hooks/useMousePopover'
import { usePersistentState } from '../hooks/usePersistentState'
import { useStore } from '../stores'

import { Header, Icon, TreeItem } from '../components'
import { MetadataFieldMenu, MetadataGroupMenu } from '../menus'
import { maxChars } from '../utils/text'

import {
    AssetMetadataField,
    BooleanMetadataField,
    FieldpickerMetadataField,
    TextListMetadataField,
    TextMetadataField,
} from './metadatafields'

export const MetadataField = observer(function MetadataField({
    language,
    item,
    metadatafield,
    metadatafieldname,
    metadatafieldpath,
    onChange,
}) {
    if (!metadatafield) {
        return null
    }

    const renderkey = item.gid + '.' + metadatafieldpath

    let Field
    switch (metadatafield.type) {
        case 'image':
            Field = (
                <AssetMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    onChange={onChange}
                />
            )
            break
        case 'boolean':
            Field = (
                <BooleanMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    onChange={onChange}
                />
            )
            break
        case 'fieldpicker':
            Field = (
                <FieldpickerMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    onChange={onChange}
                />
            )
            break
        case 'textlist':
            Field = (
                <TextListMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    onChange={onChange}
                />
            )
            break
        case 'text':
            Field = (
                <TextMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    multiline={true}
                    onChange={onChange}
                />
            )
            break
        case 'textline':
        case 'number':
        case 'decimal':
        default:
            Field = (
                <TextMetadataField
                    field={metadatafield}
                    fieldname={metadatafieldname}
                    fieldpath={metadatafieldpath}
                    item={item}
                    language={language}
                    renderkey={renderkey}
                    multiline={false}
                    onChange={onChange}
                />
            )
            break
    }

    return Field
})

const metadataname = (groupname, fieldname) => {
    if (!groupname.length || !fieldname.length) {
        return null
    }
    return groupname + '.' + fieldname
}

const original = (item, payload) => {
    const original_name = metadataname(
        payload.original_groupname,
        payload.original_fieldname
    )
    const original_field = item.metadata.get(original_name)
    const original_fieldvalue = original_field
        ? original_field.is_localized
            ? original_field.value[payload.optional_language]
            : original_field.value
        : null
    return [original_name, original_field, original_fieldvalue]
}

export const MetadataPanel = observer(function MetadataPanel({
    language,
    item,
    viewkey,
    onChange,
}) {
    const { app } = useStore()

    const MetadataContextMenu = useMousePopover(MetadataFieldMenu)
    const MetadataGroupContextMenu = useMousePopover(MetadataGroupMenu)
    const MetadataFieldContextMenu = useMousePopover(MetadataFieldMenu)

    const [selectedGroup, setSelectedGroup] = useState(null)
    const [selectedFieldPath, setSelectedFieldPath] = useState(null)

    let fieldpaths = item && item.metadata ? [...item.metadata.keys()] : []
    fieldpaths.sort()

    let initialtreestate = {}
    fieldpaths.forEach(fieldpath => {
        const fieldparts = fieldpath.split('.')
        const groupname = fieldparts.shift()
        initialtreestate[groupname] = 'collapsed'
    })
    const [treeState, setTreeState] = usePersistentState(
        viewkey + '/metatree',
        initialtreestate
    )
    const onExpandTreeState = key => {
        let newtreestate = { ...treeState }
        newtreestate[key] = 'expanded'
        setTreeState(newtreestate)
    }
    const onCollapseTreeState = key => {
        let newtreestate = { ...treeState }
        newtreestate[key] = 'collapsed'
        setTreeState(newtreestate)
    }
    if (!item) return null

    const _onSaveMetadataGroup = payload => {
        if (payload.groupname === payload.original_groupname) {
            return
        }
        const names = Array.from(item.metadata.keys())
        for (const name of names) {
            const groupname = name.split('.').shift()
            if (groupname !== payload.original_groupname) {
                continue
            }
            const original_field = item.metadata.get(name)
            const original_fieldvalue = original_field
                ? original_field.is_localized
                    ? original_field.value[payload.optional_language]
                    : original_field.value
                : null

            const rename = name
                .split('.')
                .splice(0, 1, payload.groupname) // remove old group name and replace with new name
                .join('.')

            item.setMetadataField(
                rename,
                original_field.type,
                original_field.is_localized,
                original_fieldvalue,
                language
            )
            item.clearMetadataField(name)
        }
        onChange && onChange(item)

        let newtreestate = { ...treeState }
        newtreestate[payload.groupname] = 'expanded'
        if (Object.hasOwn(newtreestate, payload.original_groupname)) {
            newtreestate[payload.groupname] = newtreestate[payload.original_groupname]
            delete newtreestate[payload.original_groupname]
        }
        setTreeState(newtreestate)
    }

    const _onDeleteMetadataGroup = payload => {
        if (!payload.original_groupname) {
            return
        }
        const names = Array.from(item.metadata.keys())
        for (const name of names) {
            const groupname = name.split('.').shift()
            if (groupname !== payload.original_groupname) {
                continue
            }
            item.clearMetadataField(name)
        }
        onChange && onChange(item)

        let newtreestate = { ...treeState }
        if (Object.hasOwn(newtreestate, payload.original_groupname)) {
            delete newtreestate[payload.original_groupname]
        }
        setTreeState(newtreestate)
    }

    const _onSaveMetadataField = payload => {
        const name = metadataname(payload.groupname, payload.fieldname)
        const [original_name, , original_fieldvalue] = original(item, payload)
        item.setMetadataField(
            name,
            payload.fieldtype,
            payload.islocalized,
            original_fieldvalue,
            language
        )
        if (original_name && name !== original_name) {
            item.clearMetadataField(original_name)
        }
        onChange && onChange(item)

        if (payload.groupname !== payload.original_groupname) {
            let newtreestate = { ...treeState }
            newtreestate[payload.groupname] = 'expanded'
            if (Object.hasOwn(newtreestate, payload.original_groupname)) {
                newtreestate[payload.groupname] =
                    newtreestate[payload.original_groupname]
                delete newtreestate[payload.original_groupname]
            }
            setTreeState(newtreestate)
        }
    }

    const _onSaveMetadataFieldCopy = payload => {
        const name = metadataname(payload.groupname, payload.fieldname)
        const [, , original_fieldvalue] = original(item, payload)
        item.setMetadataField(
            name,
            payload.fieldtype,
            payload.islocalized,
            original_fieldvalue,
            language
        )
        onChange && onChange(item)

        if (payload.groupname !== payload.original_groupname) {
            let newtreestate = { ...treeState }
            newtreestate[payload.groupname] = 'expanded'
            if (Object.hasOwn(newtreestate, payload.original_groupname)) {
                newtreestate[payload.groupname] =
                    newtreestate[payload.original_groupname]
                delete newtreestate[payload.original_groupname]
            }
            setTreeState(newtreestate)
        }
    }

    const _onDeleteMetadataField = payload => {
        const [original_name, ,] = original(item, payload)
        item.clearMetadataField(original_name)
        onChange && onChange(item)

        const groupnames = Array.from(item.metadata.keys()).map(name =>
            name.split('.').shift()
        )
        if (!groupnames.includes(payload.original_groupname)) {
            let newtreestate = { ...treeState }
            if (Object.hasOwn(newtreestate, payload.original_groupname)) {
                delete newtreestate[payload.original_groupname]
            }
            setTreeState(newtreestate)
        }
    }

    const _onCancel = () => {}

    const onMetadataAction = (action, payload) => {
        const actions = {
            save_metadata_group: _onSaveMetadataGroup,
            delete_metadata_group: _onDeleteMetadataGroup,
            save_metadata_field: _onSaveMetadataField,
            save_metadata_field_copy: _onSaveMetadataFieldCopy,
            delete_metadata_field: _onDeleteMetadataField,
            cancel: _onCancel,
        }
        if (!(action in actions)) {
            console.log(`onMetadataAction: unhandled action '${action}'`)
            return
        }
        MetadataContextMenu.hide()
        MetadataGroupContextMenu.hide()
        MetadataFieldContextMenu.hide()
        actions[action]({ optional_language: language, ...payload })
    }

    let metadata_treeitems = []
    let current_groupname
    fieldpaths.forEach(fieldpath => {
        const fieldparts = fieldpath.split('.')
        const groupname = fieldparts.shift()
        const fieldname = fieldparts.join('.')
        if (groupname !== current_groupname) {
            const onContextMenu = e => {
                setSelectedGroup(groupname)
                MetadataContextMenu.hide()
                MetadataFieldContextMenu.hide()
                MetadataGroupContextMenu.onShow(e)
            }
            const grouptreeitem = (
                <TreeItem
                    key={groupname}
                    emptyicon={'empty'}
                    indent={0}
                    selected={false}
                    state={
                        Object.hasOwn(treeState, groupname)
                            ? treeState[groupname]
                            : 'collapsed'
                    }
                    onExpand={() => {
                        onExpandTreeState(groupname)
                    }}
                    onCollapse={() => {
                        onCollapseTreeState(groupname)
                    }}
                    onContextMenu={onContextMenu}
                >
                    {maxChars(groupname, 40)}
                </TreeItem>
            )
            metadata_treeitems.push(grouptreeitem)
            current_groupname = groupname
        }
        if (treeState[groupname] === 'expanded') {
            const field = item.metadata.get(fieldpath)
            const onContextMenu = e => {
                setSelectedFieldPath(fieldpath)
                MetadataContextMenu.hide()
                MetadataGroupContextMenu.hide()
                MetadataFieldContextMenu.onShow(e)
            }
            const fieldtreeitem = (
                <div
                    key={fieldpath}
                    className="ws-propertytable"
                    style={{ paddingLeft: 18, marginBottom: 6 }}
                >
                    <div
                        className="ws-propertytable-cell ws-propertytable-cell-label"
                        onContextMenu={onContextMenu}
                    >
                        {fieldname}
                        {field.is_localized ? (
                            <span className="language">{language}</span>
                        ) : undefined}
                        <span className="action" onClick={onContextMenu}>
                            <Icon name="ellipsis" size="text" />
                        </span>
                    </div>
                    <div
                        className="ws-propertytable-cell ws-propertytable-cell-input"
                        onContextMenu={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            e.nativeEvent.stopImmediatePropagation()
                        }}
                    >
                        <MetadataField
                            language={language}
                            item={item}
                            metadatafield={field}
                            metadatafieldpath={fieldpath}
                            metadatafieldname={fieldname}
                            onChange={onChange}
                        />
                    </div>
                </div>
            )
            metadata_treeitems.push(fieldtreeitem)
        }
    })

    const field = item.metadata.get(selectedFieldPath)

    return (
        <>
            <MetadataContextMenu.Panel item={item} onAction={onMetadataAction} />
            <MetadataGroupContextMenu.Panel
                item={item}
                groupname={selectedGroup}
                onAction={onMetadataAction}
            />
            <MetadataFieldContextMenu.Panel
                item={item}
                field={field}
                fieldpath={selectedFieldPath}
                onAction={onMetadataAction}
            />
            <Header
                onContextMenu={e => {
                    MetadataFieldContextMenu.hide()
                    MetadataGroupContextMenu.hide()
                    MetadataContextMenu.onShow(e)
                }}
            >
                {app.text('Metadata')}
            </Header>
            {metadata_treeitems}
        </>
    )
})
